import { addParamsToUrl } from 'utils/query';

import { eventApi } from '../event';
import { companyApi } from '../company';

import {
  BookingRep,
  BookingList,
  CopyRequest,
  BookingFields,
  BookingStatus,
  BookingDetails,
  CopyCandidates,
  BookingPlanRow,
  BookingYearPlan,
  BookingListRequest,
  BookingCancelReason,
  BookingLastYearList,
  ChangeStatusResponse,
  CreateBookingRequest,
  BookingYearPlanRequest,
  MarkBookingPaidRequest,
  BookingPaymentsRequest,
  BookingPaymentsOverview,
  AddBookingPaymentRequest,
} from './types';

// BOOKING (owner 25 Oct 2026): /api/v1/bookings. Injected into the company
// API so the store's reducer list stays as it is (like time off); one
// "Booking" tag refreshes the queue, the Year Plan and the panel after any
// change. Booked or linked also refreshes the Events lists.
const TAG = 'Booking';
// Last year's sales and profit change only when shows are copied in.
const LAST_YEAR_TAG = 'BookingLastYear';

// The pages show "off" or "no longer exists" themselves: a 404 from a
// booking read never sends the user to the Not found page.
const headers = { 'allow-not-found': 'true' };

const refreshEvents = async (
  _: unknown,
  {
    dispatch,
    queryFulfilled,
  }: {
    queryFulfilled: Promise<unknown>;
    dispatch: (action: unknown) => unknown;
  }
) => {
  try {
    await queryFulfilled;
    dispatch(eventApi.util.invalidateTags(['Event']));
  } catch {
    // The error is shown by the caller.
  }
};

export const bookingApi = companyApi
  .enhanceEndpoints({ addTagTypes: [TAG, LAST_YEAR_TAG] })
  .injectEndpoints({
    endpoints: (build) => ({
      bookingReps: build.query<BookingRep[], void>({
        providesTags: [TAG],
        query: () => ({ headers, method: 'get', url: 'bookings/reps' }),
      }),
      bookingDetails: build.query<BookingDetails, number>({
        providesTags: [TAG],
        query: (id) => ({ headers, method: 'get', url: `bookings/${id}` }),
      }),
      createBooking: build.mutation<BookingDetails, CreateBookingRequest>({
        invalidatesTags: [TAG],
        query: (data) => ({ data, method: 'post', url: 'bookings' }),
      }),
      bookingList: build.query<BookingList, BookingListRequest>({
        providesTags: [TAG],
        query: (params) => ({
          headers,
          method: 'get',
          url: addParamsToUrl('bookings', { ...params }),
        }),
      }),
      bookingYearPlan: build.query<BookingYearPlan, BookingYearPlanRequest>({
        providesTags: [TAG],
        query: (params) => ({
          headers,
          method: 'get',
          url: addParamsToUrl('bookings/year-plan', { ...params }),
        }),
      }),
      editBooking: build.mutation<
        BookingDetails,
        { id: number; data: BookingFields }
      >({
        invalidatesTags: [TAG],
        query: ({ id, data }) => ({
          data,
          method: 'patch',
          url: `bookings/${id}`,
        }),
      }),
      // The Location form: which of its payment rows Booking manages.
      bookingEventPayments: build.query<
        { bookingPaymentIds: number[] },
        number
      >({
        providesTags: [TAG],
        query: (eventId) => ({
          headers,
          method: 'get',
          url: `bookings/event-payments/${eventId}`,
        }),
      }),
      assignBookingRep: build.mutation<
        BookingDetails,
        { id: number; repId: number | null }
      >({
        invalidatesTags: [TAG],
        query: ({ id, repId }) => ({
          method: 'patch',
          data: { repId },
          url: `bookings/${id}/rep`,
        }),
      }),
      // BOOKING A2: payments. A change can move a Location's payment
      // rows, so the Events lists refresh too.
      bookingPayments: build.query<
        BookingPaymentsOverview,
        BookingPaymentsRequest
      >({
        providesTags: [TAG],
        query: (params) => ({
          headers,
          method: 'get',
          url: addParamsToUrl('bookings/payments', { ...params }),
        }),
      }),
      // Sales and profit last year: read apart so the plan never waits.
      bookingLastYear: build.query<BookingLastYearList, { year: number }>({
        keepUnusedDataFor: 600,
        providesTags: [LAST_YEAR_TAG],
        query: (params) => ({
          headers,
          method: 'get',
          url: addParamsToUrl('bookings/last-year', { ...params }),
        }),
      }),
      bookingCopyCandidates: build.query<
        CopyCandidates,
        { toYear: number; fromYear: number }
      >({
        providesTags: [TAG],
        query: (params) => ({
          headers,
          method: 'get',
          url: addParamsToUrl('bookings/copy-candidates', { ...params }),
        }),
      }),
      addBookingPayment: build.mutation<
        BookingDetails,
        { id: number; data: AddBookingPaymentRequest }
      >({
        invalidatesTags: [TAG],
        onQueryStarted: refreshEvents,
        query: ({ id, data }) => ({
          data,
          method: 'post',
          url: `bookings/${id}/payments`,
        }),
      }),
      copyBookings: build.mutation<
        { added: number; toYear: number; linked: number },
        CopyRequest
      >({
        onQueryStarted: refreshEvents,
        invalidatesTags: [TAG, LAST_YEAR_TAG],
        query: (data) => ({
          data,
          method: 'post',
          url: 'bookings/copy-from-year',
        }),
      }),
      saveBookingPlan: build.mutation<
        BookingDetails,
        { id: number; total: number; rows: BookingPlanRow[] }
      >({
        invalidatesTags: [TAG],
        onQueryStarted: refreshEvents,
        query: ({ id, ...data }) => ({
          data,
          method: 'put',
          url: `bookings/${id}/payment-plan`,
        }),
      }),
      linkBookingLocation: build.mutation<
        ChangeStatusResponse,
        { id: number; eventId: number }
      >({
        invalidatesTags: [TAG],
        onQueryStarted: refreshEvents,
        query: ({ id, eventId }) => ({
          method: 'patch',
          data: { eventId },
          url: `bookings/${id}/link-location`,
        }),
      }),
      markBookingPaid: build.mutation<
        BookingDetails,
        { paymentId: number; data: MarkBookingPaidRequest }
      >({
        invalidatesTags: [TAG],
        onQueryStarted: refreshEvents,
        query: ({ data, paymentId }) => ({
          data,
          method: 'patch',
          url: `bookings/payments/${paymentId}/mark-paid`,
        }),
      }),
      markBookingCashed: build.mutation<
        BookingDetails,
        { paymentId: number; paymentMethodId?: number | null }
      >({
        invalidatesTags: [TAG],
        onQueryStarted: refreshEvents,
        query: ({ paymentId, ...data }) => ({
          data,
          method: 'patch',
          url: `bookings/payments/${paymentId}/cashed`,
        }),
      }),
      moveBooking: build.mutation<
        BookingList,
        {
          id: number;
          toTop?: boolean;
          afterId?: number | null;
          beforeId?: number | null;
        }
      >({
        invalidatesTags: [TAG],
        query: ({ id, ...data }) => ({
          data,
          method: 'patch',
          url: `bookings/${id}/priority`,
        }),
      }),
      changeBookingStatus: build.mutation<
        ChangeStatusResponse,
        {
          id: number;
          confirmNew?: boolean;
          status: BookingStatus;
          cancelNote?: string | null;
          cancelReason?: BookingCancelReason;
        }
      >({
        invalidatesTags: [TAG],
        onQueryStarted: refreshEvents,
        query: ({ id, ...data }) => ({
          data,
          method: 'patch',
          url: `bookings/${id}/status`,
        }),
      }),
    }),
  });

export const {
  useBookingRepsQuery,
  useBookingListQuery,
  useEditBookingMutation,
  useMoveBookingMutation,
  useBookingDetailsQuery,
  useBookingLastYearQuery,
  useCopyBookingsMutation,
  useBookingYearPlanQuery,
  useBookingPaymentsQuery,
  useCreateBookingMutation,
  useSaveBookingPlanMutation,
  useMarkBookingPaidMutation,
  useAssignBookingRepMutation,
  useAddBookingPaymentMutation,
  useBookingEventPaymentsQuery,
  useMarkBookingCashedMutation,
  useBookingCopyCandidatesQuery,
  useChangeBookingStatusMutation,
  useLinkBookingLocationMutation,
} = bookingApi;
